import { useMemo, useState } from "react";
import { rankDone, rankHot, rankNew, type RankComic } from "@/mocks/comicData";
import SectionHeading from "@/components/feature/SectionHeading";

type TabKey = "hot" | "done" | "new";

const tabs: { key: TabKey; label: string }[] = [
  { key: "hot", label: "热门榜" },
  { key: "done", label: "完结榜" },
  { key: "new", label: "新作榜" },
];

const trendIcon = {
  up: { icon: "ri-arrow-up-line", cls: "text-primary-600" },
  down: { icon: "ri-arrow-down-line", cls: "text-foreground-400" },
  same: { icon: "ri-subtract-line", cls: "text-foreground-300" },
} as const;

interface RankingProps {
  searchQuery: string;
}

export default function Ranking({ searchQuery }: RankingProps) {
  const [tab, setTab] = useState<TabKey>("hot");
  const sources: Record<TabKey, RankComic[]> = { hot: rankHot, done: rankDone, new: rankNew };

  const list = useMemo(() => {
    const base = sources[tab];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return base;
    return base.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        c.genre.toLowerCase().includes(q) ||
        c.authors.toLowerCase().includes(q),
    );
  }, [tab, searchQuery]);

  return (
    <section id="ranking" className="scroll-mt-16 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="每日更新榜单"
          title="动漫之家漫画网排行榜"
          subtitle="热门、完结与新作三大榜单实时更新，用评分和人气帮你筛出真正值得追的高分作品。"
        />

        <div className="mb-8 flex flex-col items-center justify-between gap-4 md:flex-row">
          <div className="inline-flex rounded-full bg-background-100 p-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                  tab === t.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {searchQuery.trim() && (
            <span className="text-sm text-foreground-500">
              搜索「{searchQuery}」的结果
            </span>
          )}
        </div>

        {list.length === 0 ? (
          <div className="rounded-lg border border-background-200 bg-background-100 py-16 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-300"></i>
            <p className="mt-3 text-sm text-foreground-500">没有找到匹配「{searchQuery}」的作品，换个关键词试试。</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {list.map((comic, idx) => (
              <RankingCard key={comic.id} comic={comic} rank={idx + 1} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function RankingCard({ comic, rank }: { comic: RankComic; rank: number }) {
  const t = trendIcon[comic.trend];
  return (
    <article className="group relative overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-transform duration-300 hover:-translate-y-1">
      <div className="relative aspect-[3/4] w-full overflow-hidden">
        <img
          src={comic.imageUrl}
          alt={`${comic.title} 漫画封面 - 动漫之家漫画网排行榜第${rank}名`}
          title={`${comic.title} · 动漫之家漫画网排行榜`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 via-transparent to-transparent" />
        <span
          className={`absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-md text-sm font-bold ${
            rank <= 3 ? "bg-accent-500 text-background-50" : "bg-background-950/70 text-background-100 backdrop-blur"
          }`}
        >
          {rank}
        </span>
        <span className="absolute right-2 top-2 rounded-md bg-primary-500 px-2 py-1 text-[11px] font-medium text-background-50">
          {comic.tag}
        </span>
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-background-50">
          <span className="flex items-center gap-1 text-sm font-bold">
            <i className="ri-star-fill text-accent-500"></i>
            {comic.rating}
          </span>
          {comic.update && <span className="text-[11px] text-background-100">{comic.update}</span>}
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between gap-1">
          <h3 className="truncate text-sm font-bold text-foreground-900">{comic.title}</h3>
          <i className={`text-sm ${t.cls}`}></i>
        </div>
        <p className="mt-1 flex items-center gap-2 text-xs text-foreground-600">
          <span>{comic.genre}</span>
          <span className="text-foreground-300">·</span>
          <span>{comic.chapters}</span>
        </p>
      </div>
    </article>
  );
}